import type { AxiosPromise } from 'axios';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { IOperationsService } from './IOperationsService';
import { OperationsEndpoints } from './operationsEndpoints';
import {
    mapOperationActivityDetail,
    mapOperationActivityPage,
    mapOperationCatalog,
    mapOperationCategories,
    mapOperationTemplateHistory,
    mapOperationPreview,
    mapOperationTemplateDetail,
    mapRecordableOperations,
    mapRecordedOperation,
    toLineWire,
    toRecordWire,
    type OperationActivityDetailWire,
    type OperationActivityPageWire,
    type OperationCatalogWire,
    type OperationCategoriesWire,
    type OperationTemplateHistoryWire,
    type OperationPreviewWire,
    type OperationTemplateDetailWire,
    type RecordableOperationsWire,
    type RecordedOperationWire,
} from './operationsMapping';
import type { OperationActivityFilters } from '@/api/models';
import type {
    CreateOperationCategoryRequest,
    CreateOperationTemplateRequest,
    PreviewOperationRequest,
    RecordOperationRequest,
    SaveOperationDraftRequest,
    UpdateOperationCategoryRequest,
} from './requests';
import type {
    GetOperationActivityDetailResponse,
    GetOperationActivityResponse,
    GetOperationCatalogResponse,
    GetOperationCategoriesResponse,
    GetOperationHistoryResponse,
    GetOperationTemplateResponse,
    GetRecordableOperationsResponse,
    PreviewOperationResponse,
    RecordOperationResponse,
} from './responses';

type OperationsServiceOptions = {
    baseUrl: string;
};

/**
 * A body this client could not read as a catalog at all — no `items` array — is
 * contract drift, not a list to guess at. Mirrors `AccountsService`'s
 * `UNMAPPABLE_ACCOUNT` and `TrialBalanceService`'s `UNMAPPABLE_TRIAL_BALANCE`.
 *
 * The bar is deliberately lower here than on the trial balance. There, a single
 * unreadable row fails the whole read, because the totals printed beneath the
 * rows are the server's and a dropped row makes them disagree. A catalog has no
 * totals and no arithmetic, so an unreadable *row* is dropped and the rest is
 * served; only a body that is not a catalog at all reaches this.
 */
/**
 * A body that is not a category list at all.
 *
 * An individual unreadable category is dropped rather than fatal — a chip is a
 * label, and losing the whole set over one bad row would cost the editor its
 * category control.
 */
const UNMAPPABLE_CATEGORIES: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned operation categories this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_CATEGORIES',
    },
};

const UNMAPPABLE_CATALOG: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned an operation catalog this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_CATALOG',
    },
};

/**
 * A template body this client cannot read in full.
 *
 * The bar here is the trial balance's, not the catalog's: a recipe with one line
 * silently dropped still renders — as a different recipe, one whose pairs no
 * longer balance — and the editor would then publish that as the version entries
 * post under. The page's error-with-retry state is the correct outcome.
 */
const UNMAPPABLE_TEMPLATE: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message: 'The server returned an operation this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_TEMPLATE',
    },
};

/**
 * A recordable list this client cannot read at all — no `date`, or no `items`
 * array.
 *
 * The catalog's bar, not the trial balance's: an individual operation that
 * cannot be read is dropped from the picker, and only a body that is not a list
 * reaches this.
 */
const UNMAPPABLE_RECORDABLE: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned a list of recordable operations this client cannot read.',
        code: 'UNMAPPABLE_RECORDABLE_OPERATIONS',
    },
};

/**
 * A preview this client cannot read in full.
 *
 * The strictest bar on this resource, and the reason is the panel it feeds: the
 * impact panel prints the server's own totals under the lines it received, so a
 * dropped line renders an entry that does not add up — under a Post button. A
 * preview that cannot be read must leave the panel empty and Post disabled.
 */
const UNMAPPABLE_POSTING: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned a posting preview this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_PREVIEW',
    },
};

/**
 * A recording result this client cannot read.
 *
 * Distinct from the preview's on purpose, because the consequence is the
 * opposite: **the entry is posted**. This is a failure to read the receipt, not
 * a failure to book, and the page must not invite a retry as though nothing had
 * happened — replaying the same idempotency key would return this same
 * unreadable body forever.
 */
const UNMAPPABLE_RECORDED: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The operation was posted, but the server returned a result this client ' +
            'cannot read. Check the Journal before recording it again.',
        code: 'UNMAPPABLE_RECORDED_OPERATION',
    },
};

/**
 * An activity page this client cannot read at all — no `items` array.
 *
 * The catalog's bar: an individual row that cannot be read is dropped and the
 * rest of the register is served, because there are no totals for a missing row
 * to contradict.
 */
const UNMAPPABLE_ACTIVITY: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned an activity list this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_ACTIVITY',
    },
};

/**
 * A recorded operation this client cannot read in full.
 *
 * The trial balance's bar, because the drawer prints the entry under its own
 * totals and puts a Reverse button beside it. An entry whose lines do not add up
 * to the total above them is the one thing an audit view must never render, and
 * the dialog behind that button mirrors those very lines.
 */
const UNMAPPABLE_ACTIVITY_DETAIL: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned a recorded operation this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_ACTIVITY_DETAIL',
    },
};

/**
 * A history this client cannot read in full.
 *
 * The strictest bar on this resource alongside the preview's, and for a reason
 * of its own: these line sets are the two halves of a **diff**. A version
 * rendered short one line produces a diff claiming that line was removed — an
 * audit view asserting a configuration change that never happened.
 */
const UNMAPPABLE_HISTORY: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned a version history this client cannot read.',
        code: 'UNMAPPABLE_OPERATION_HISTORY',
    },
};

/**
 * The live operations client (bos-api).
 *
 * Maps at the boundary via `operationsMapping`, so nothing downstream sees a
 * wire shape and the server's field names are confined to that one file.
 *
 * What this service deliberately does **not** do:
 *
 * - It does not sort. Templates-before-system is the server's order and carries
 *   meaning (the rows you can act on lead); re-sorting here would be a second
 *   opinion about it.
 * - It does not filter or search. The page does, over the whole list.
 * - It does not decide what the caller may edit or record. `rolesAllowed` is a
 *   fact about the template; what this session may do with it is
 *   `operationsAccess`'s call, and the server's again at record time.
 * - It does not reverse anything. Reversing a recorded operation is the
 *   journal's `reverseEntry` on the ordinary entry it posted, which is why task
 *   03 made it an ordinary entry; this resource only reports the reversal.
 * - It does not compute a posting. `previewOperation` asks the server what the
 *   entry would be, rather than deriving it from a recipe client-side. The
 *   editor's `operationPreview.ts` does derive one, and the two answer different
 *   questions: that is "what would this *recipe* book for a sample?", this is
 *   "what will *this request* book, with these accounts, on this date, at the
 *   rate in force then". Only the server can answer the second, and it is the
 *   one a Post button sits under.
 */
export class OperationsService implements IOperationsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: OperationsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getCatalog(): Promise<
        ApiResponse<GetOperationCatalogResponse>
    > {
        const result = await makeApiRequest<OperationCatalogWire>(
            this.client.get(OperationsEndpoints.catalog()),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationCatalog(result.response);
        if (!mapped) {
            return UNMAPPABLE_CATALOG;
        }
        return { success: true, response: mapped };
    }
    public async getCategories(): Promise<
        ApiResponse<GetOperationCategoriesResponse>
    > {
        const result = await makeApiRequest<OperationCategoriesWire>(
            this.client.get(OperationsEndpoints.categories()),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationCategories(result.response);
        if (!mapped) {
            return UNMAPPABLE_CATEGORIES;
        }
        return { success: true, response: mapped };
    }

    public async createCategory(
        body: CreateOperationCategoryRequest,
    ): Promise<ApiResponse<GetOperationCategoriesResponse>> {
        return this.readCategories(
            this.client.post(OperationsEndpoints.createCategory(), {
                nameKa: body.nameKa,
                nameEn: body.nameEn,
            }),
        );
    }

    public async updateCategory(
        id: string,
        body: UpdateOperationCategoryRequest,
    ): Promise<ApiResponse<GetOperationCategoriesResponse>> {
        return this.readCategories(
            this.client.put(OperationsEndpoints.updateCategory(id), {
                nameKa: body.nameKa,
                nameEn: body.nameEn,
            }),
        );
    }

    public async deleteCategory(id: string): Promise<ApiResponse<void>> {
        // No mapping and no body: a 204 carries nothing, and the refusal the
        // caller actually branches on — `HAS_DEPENDENTS` with its counts — rides
        // the error envelope, which `makeApiRequest` already reads.
        return makeApiRequest<void>(
            this.client.delete(OperationsEndpoints.deleteCategory(id)),
        );
    }

    /**
     * The shared tail of the three category calls: map the list, or say the body
     * could not be read.
     *
     * Both writes answer the same shape the read does, so they go through the
     * same mapping — a create that returned a list this client could not parse
     * is the same failure as a read that did.
     */
    private async readCategories(
        request: AxiosPromise,
    ): Promise<ApiResponse<GetOperationCategoriesResponse>> {
        const result = await makeApiRequest<OperationCategoriesWire>(request);
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationCategories(result.response);
        if (!mapped) {
            return UNMAPPABLE_CATEGORIES;
        }
        return { success: true, response: mapped };
    }

    public async getTemplate(
        key: string,
    ): Promise<ApiResponse<GetOperationTemplateResponse>> {
        return this.readTemplate(
            this.client.get(OperationsEndpoints.template(key)),
        );
    }

    public async createTemplate(
        body: CreateOperationTemplateRequest,
    ): Promise<ApiResponse<GetOperationTemplateResponse>> {
        return this.readTemplate(
            this.client.post(OperationsEndpoints.create(), {
                nameKa: body.nameKa,
                nameEn: body.nameEn,
                // `undefined` is dropped by JSON.stringify, which is what the
                // server reads as "file it wherever you file the unfiled" — the
                // residual category. It refuses nothing over this field.
                categoryId: body.categoryId,
            }),
        );
    }

    public async saveDraft(
        key: string,
        body: SaveOperationDraftRequest,
    ): Promise<ApiResponse<GetOperationTemplateResponse>> {
        return this.readTemplate(
            this.client.post(OperationsEndpoints.versions(key), {
                effectiveFrom: body.effectiveFrom,
                rolesAllowed: body.rolesAllowed,
                lines: body.lines.map(toLineWire),
                // `undefined` when the caller did not touch the name —
                // JSON.stringify drops the key entirely, which is exactly
                // what tells the server "leave it alone".
                nameKa: body.nameKa,
                nameEn: body.nameEn,
                // Same treatment, and applied to the template rather than
                // versioned: a category is a label on the operation's identity,
                // not a term in its posting recipe.
                categoryId: body.categoryId,
            }),
        );
    }

    public async activateVersion(
        key: string,
        version: number,
        effectiveFrom?: string,
    ): Promise<ApiResponse<GetOperationTemplateResponse>> {
        return this.readTemplate(
            // An omitted date is sent as an omitted field, not as null: the
            // server reads absence as "keep the draft's own date", and null
            // would be a value it has to decide about.
            this.client.post(
                OperationsEndpoints.activate(key, version),
                effectiveFrom ? { effectiveFrom } : {},
            ),
        );
    }

    public async setTemplateBlocked(
        key: string,
        blocked: boolean,
    ): Promise<ApiResponse<GetOperationTemplateResponse>> {
        const url = blocked
            ? OperationsEndpoints.block(key)
            : OperationsEndpoints.unblock(key);
        return this.readTemplate(this.client.post(url, {}));
    }

    public async getRecordable(
        date: string,
    ): Promise<ApiResponse<GetRecordableOperationsResponse>> {
        const result = await makeApiRequest<RecordableOperationsWire>(
            this.client.get(OperationsEndpoints.recordable(date)),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapRecordableOperations(result.response);
        if (!mapped) {
            return UNMAPPABLE_RECORDABLE;
        }
        return { success: true, response: mapped };
    }

    public async previewOperation(
        key: string,
        body: PreviewOperationRequest,
    ): Promise<ApiResponse<PreviewOperationResponse>> {
        const result = await makeApiRequest<OperationPreviewWire>(
            this.client.post(OperationsEndpoints.preview(key), {
                date: body.date,
                amountTetri: body.amountTetri,
                slots: body.slots,
            }),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationPreview(result.response);
        if (!mapped) {
            return UNMAPPABLE_POSTING;
        }
        return { success: true, response: mapped };
    }

    public async recordOperation(
        key: string,
        body: RecordOperationRequest,
    ): Promise<ApiResponse<RecordOperationResponse>> {
        const result = await makeApiRequest<RecordedOperationWire>(
            this.client.post(
                OperationsEndpoints.record(key),
                toRecordWire(body),
            ),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapRecordedOperation(result.response);
        if (!mapped) {
            // The entry is posted either way — this failure is about reading the
            // receipt, not about the booking. The page says so rather than
            // inviting a retry that would replay the same idempotency key and
            // return this same unreadable body.
            return UNMAPPABLE_RECORDED;
        }
        return { success: true, response: mapped };
    }

    public async getActivity(
        filters: OperationActivityFilters,
    ): Promise<ApiResponse<GetOperationActivityResponse>> {
        // Every filter is optional, and an omitted one must be *absent* rather
        // than blank: the server reads `key=` as a filter on the empty string
        // and answers with nothing at all.
        const params = new URLSearchParams();
        if (filters.from) {
            params.set('from', filters.from);
        }
        if (filters.to) {
            params.set('to', filters.to);
        }
        if (filters.key) {
            params.set('key', filters.key);
        }
        if (filters.status) {
            params.set('status', filters.status);
        }
        if (filters.page) {
            params.set('page', String(filters.page));
        }
        if (filters.pageSize) {
            params.set('pageSize', String(filters.pageSize));
        }

        const result = await makeApiRequest<OperationActivityPageWire>(
            this.client.get(OperationsEndpoints.activity(params)),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationActivityPage(result.response);
        if (!mapped) {
            return UNMAPPABLE_ACTIVITY;
        }
        return { success: true, response: mapped };
    }

    public async getActivityDetail(
        recordId: string,
        locale?: string,
    ): Promise<ApiResponse<GetOperationActivityDetailResponse>> {
        const result = await makeApiRequest<OperationActivityDetailWire>(
            this.client.get(
                OperationsEndpoints.activityDetail(recordId, locale),
            ),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationActivityDetail(result.response);
        if (!mapped) {
            return UNMAPPABLE_ACTIVITY_DETAIL;
        }
        return { success: true, response: mapped };
    }

    public async getHistory(
        key: string,
    ): Promise<ApiResponse<GetOperationHistoryResponse>> {
        const result = await makeApiRequest<OperationTemplateHistoryWire>(
            this.client.get(OperationsEndpoints.history(key)),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationTemplateHistory(result.response);
        if (!mapped) {
            return UNMAPPABLE_HISTORY;
        }
        return { success: true, response: mapped };
    }

    /**
     * Every call on this resource but the catalog answers with the template as
     * it now stands, so they share one unwrap. Keeping it in one place is also
     * what stops a new write quietly skipping the mapping and handing a wire
     * shape to the editor.
     */
    private async readTemplate(
        request: AxiosPromise<OperationTemplateDetailWire>,
    ): Promise<ApiResponse<GetOperationTemplateResponse>> {
        const result =
            await makeApiRequest<OperationTemplateDetailWire>(request);
        if (!result.success) {
            return result;
        }
        const mapped = mapOperationTemplateDetail(result.response);
        if (!mapped) {
            return UNMAPPABLE_TEMPLATE;
        }
        return { success: true, response: mapped };
    }
}
